import { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function LeadForm({ source = "contact", testid = "lead-form" }) {
  const isProcess = source === "process";
  const [form, setForm] = useState({
    name: "", email: "", company: "", role_title: "", area: "", country: "", org_size: "",
    message: "", consent: false, website: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Please enter your name.";
    if (!EMAIL_RE.test(form.email)) er.email = "Please enter a valid work email.";
    if (form.message.trim().length < 10) er.message = isProcess
      ? "Please describe the process or problem (at least a sentence)."
      : "Please add a short message (at least a sentence).";
    if (!form.consent) er.consent = "Please consent to storing your enquiry.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, source });
      setDone(true);
      toast.success("Thank you — your enquiry has been received.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid={`${testid}-success`} className="border border-[#E5E5E5] bg-off-white p-10 text-center">
        <CheckCircle2 className="mx-auto text-restrained-blue" size={40} />
        <h3 className="font-serif text-2xl text-deep-navy mt-5">Enquiry received</h3>
        <p className="text-charcoal/70 mt-3 max-w-md mx-auto">
          Thank you. A member of the ThinkStack team will review your enquiry and respond directly. We store your details only to respond to this request.
        </p>
      </div>
    );
  }

  const field = "w-full border border-[#E5E5E5] bg-white px-4 py-3 text-charcoal placeholder:text-charcoal/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-restrained-blue transition-shadow";
  const errCls = "border-destructive";

  return (
    <form onSubmit={onSubmit} data-testid={testid} noValidate className="space-y-5">
      {/* honeypot */}
      <input type="text" value={form.website} onChange={set("website")} name="website" tabIndex={-1}
             autoComplete="off" className="hidden" aria-hidden="true" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="overline text-charcoal/60 block mb-2" htmlFor="lf-name">Full name *</label>
          <input id="lf-name" data-testid={`${testid}-name`} className={`${field} ${errors.name ? errCls : ""}`}
                 value={form.name} onChange={set("name")} placeholder="Jane Tan" />
          {errors.name && <p className="text-destructive text-xs mt-1">{errors.name}</p>}
        </div>
        <div>
          <label className="overline text-charcoal/60 block mb-2" htmlFor="lf-email">Work email *</label>
          <input id="lf-email" data-testid={`${testid}-email`} type="email" className={`${field} ${errors.email ? errCls : ""}`}
                 value={form.email} onChange={set("email")} placeholder="jane@company.com" />
          {errors.email && <p className="text-destructive text-xs mt-1">{errors.email}</p>}
        </div>
        <div>
          <label className="overline text-charcoal/60 block mb-2" htmlFor="lf-company">Company</label>
          <input id="lf-company" data-testid={`${testid}-company`} className={field}
                 value={form.company} onChange={set("company")} placeholder="Company name" />
        </div>
        <div>
          <label className="overline text-charcoal/60 block mb-2" htmlFor="lf-role">Job title</label>
          <input id="lf-role" data-testid={`${testid}-role`} className={field}
                 value={form.role_title} onChange={set("role_title")} placeholder="e.g. COO" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div>
          <label className="overline text-charcoal/60 block mb-2" htmlFor="lf-area">Area of interest</label>
          <select id="lf-area" data-testid={`${testid}-area`} className={field} value={form.area} onChange={set("area")}>
            <option value="">Select…</option>
            <option>Supply Chain</option>
            <option>Marketing & Customer</option>
            <option>Learning & Education</option>
            <option>Enterprise AI & Automation</option>
            <option>Other</option>
          </select>
        </div>
        <div>
          <label className="overline text-charcoal/60 block mb-2" htmlFor="lf-country">Country</label>
          <select id="lf-country" data-testid={`${testid}-country`} className={field} value={form.country} onChange={set("country")}>
            <option value="">Select…</option>
            <option>Singapore</option><option>Malaysia</option><option>Indonesia</option>
            <option>Vietnam</option><option>Philippines</option><option>Thailand</option><option>Other</option>
          </select>
        </div>
        <div>
          <label className="overline text-charcoal/60 block mb-2" htmlFor="lf-size">Organisation size</label>
          <select id="lf-size" data-testid={`${testid}-size`} className={field} value={form.org_size} onChange={set("org_size")}>
            <option value="">Select…</option>
            <option>1–50</option><option>51–200</option><option>201–1,000</option><option>1,000–5,000</option><option>5,000+</option>
          </select>
        </div>
      </div>

      <div>
        <label className="overline text-charcoal/60 block mb-2" htmlFor="lf-message">
          {isProcess ? "Describe the process or business problem *" : "Message *"}
        </label>
        <textarea id="lf-message" data-testid={`${testid}-message`} rows={isProcess ? 6 : 5}
                  className={`${field} resize-none ${errors.message ? errCls : ""}`}
                  value={form.message} onChange={set("message")}
                  placeholder={isProcess
                    ? "Tell us about the process, where it breaks down, and the outcome you are accountable for."
                    : "How can we help?"} />
        {errors.message && <p className="text-destructive text-xs mt-1">{errors.message}</p>}
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <input type="checkbox" data-testid={`${testid}-consent`} checked={form.consent} onChange={set("consent")}
               className="mt-1 h-4 w-4 accent-restrained-blue" />
        <span className="text-sm text-charcoal/70 leading-relaxed">
          I consent to ThinkStack storing the details in this form to respond to my enquiry. We do not share them or use them for unrelated marketing.
        </span>
      </label>
      {errors.consent && <p className="text-destructive text-xs">{errors.consent}</p>}

      <button type="submit" data-testid={`${testid}-submit`} disabled={submitting}
              className="inline-flex items-center gap-2 bg-deep-navy text-white px-8 py-3.5 text-sm hover:bg-restrained-blue transition-colors disabled:opacity-60">
        {submitting && <Loader2 className="animate-spin" size={16} />}
        {isProcess ? "Discuss My Requirement" : "Discuss My Requirement"}
      </button>
    </form>
  );
}
